import { inject } from 'mobx-react';
import * as React from 'react';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { PropertyRoomScoreInfoModel, RoomViewModel } from 'models';

import { RoomDetailsAmenities } from './room-details-amenities';
import { RoomDetailsInfoDialogLink } from './room-details-info-dialog-link';
import { RoomDetailsRoomScore } from './room-details-room-score';
import { RoomDetailsRoomSummary } from './room-details-room-summary';
import { RoomRateOptions } from 'components/infosite/rooms-and-rates/room-rate-options';
import { RoomTitle } from 'components/infosite/rooms-and-rates/room-title';

export interface RoomDetailsInterface {
  amenityClasses?: string;
  dataStid?: string;
  infoDialogClasses?: string;
  position?: string;
  room: RoomViewModel;
  currentHotel?: CurrentHotelStore;
  hideRoomTitleToA11y?: boolean;
}

@inject('currentHotel')
export class RoomDetails extends React.PureComponent<RoomDetailsInterface> {
  public render() {
    const { room, currentHotel, position, amenityClasses, infoDialogClasses, dataStid, hideRoomTitleToA11y } = this.props;
    const { name, roomSummary, roomTypeCode } = room;

    const defaultProps = {
      room,
      name,
      position,
    };

    return (
      <RoomDetailsContent
        defaultProps={defaultProps}
        roomScoreInfo={currentHotel.getRoomScore(roomTypeCode)}
        roomSummary={roomSummary}
        room={room}
        name={name}
        amenityClasses={amenityClasses}
        infoDialogClasses={infoDialogClasses}
        dataStid={dataStid}
        position={position}
        displayMultipleRatePlansInline={currentHotel?.canDisplayMultipleRatePlansInline}
        hideRoomTitleToA11y={hideRoomTitleToA11y}
      />
    );
  }
}

export interface RoomDetailsContentProps {
  displayMultipleRatePlansInline?: boolean;
  hideRoomTitleToA11y?: boolean;
  roomScoreInfo?: PropertyRoomScoreInfoModel;
  roomSummary?: string;
  room?: RoomViewModel;
  name?: any;
  amenityClasses?: string;
  infoDialogClasses?: string;
  dataStid?: string;
  position?: string;
  defaultProps?: {
    room: RoomViewModel;
    name?: any;
    position?: string;
  };
}

export const RoomDetailsContent: React.FC<RoomDetailsContentProps> = ({
  displayMultipleRatePlansInline,
  hideRoomTitleToA11y,
  defaultProps,
  roomScoreInfo,
  roomSummary,
  room,
  name,
  amenityClasses,
  infoDialogClasses,
  dataStid,
  position,
}: RoomDetailsContentProps) => {
  if (displayMultipleRatePlansInline) {
    return (
      <>
        <TitleWithMessage {...defaultProps} hideRoomTitleToA11y={hideRoomTitleToA11y} />
        {roomScoreInfo && <RoomDetailsRoomScore roomScore={roomScoreInfo.score} />}
        <RoomDetailsRoomSummary roomSummary={roomSummary} />
        <RoomDetailsAmenities className={amenityClasses} room={room} />
        <RoomDetailsInfoDialogLink className={infoDialogClasses} dataStid={dataStid} position={position} room={room} />
      </>
    );
  }

  return (
    <>
      <RoomTitle name={name} hideRoomTitleToA11y={hideRoomTitleToA11y} />
      {roomScoreInfo && <RoomDetailsRoomScore roomScore={roomScoreInfo.score} />}
      <RoomDetailsRoomSummary roomSummary={roomSummary} />
      <RoomDetailsAmenities className={amenityClasses} room={room} />
      <RoomDetailsInfoDialogLink className={infoDialogClasses} dataStid={dataStid} position={position} room={room} />
    </>
  );
};

export interface TitleWithMessageProps {
  hideRoomTitleToA11y?: boolean;
  position?: string;
  room: RoomViewModel;
  name?: any;
}

export const TitleWithMessage: React.FC<TitleWithMessageProps> = ({ room, name, position, hideRoomTitleToA11y }) => (
  <div className={`uitk-grid`}>
    <div className={`uitk-cell all-cell-fill`}>
      <RoomTitle name={name} hideRoomTitleToA11y={hideRoomTitleToA11y} />
    </div>
    {room.unavailableRatePlans && room.unavailableRatePlans.length > 0 && (
      <div className={`uitk-cell all-cell-shrink`}>
        <RoomRateOptions
          room={room}
          position={position}
          ratePlanCollection={room.unavailableRatePlans}
          id="infosite-room-unavailableRatePlans"
          isUnavailable={true}
        />
      </div>
    )}
  </div>
);
